<template>
  <div class="walkup-modal">
    <!-- Step 1: Add Walkup -->
    <div v-if="step === 1" class="walkup-step-1">
      <div class="walkup-header">
        <h1>Add Walkup</h1>
      </div>

      <div class="walkup-body">
        <div class="tiers-column">
          <div class="tier-list">
            <div
              v-for="tier in priceTiers"
              :key="tier.id"
              class="tier-row"
              :class="{ 'tier-row-sliding': tier.feeType === 'sliding' }"
            >
              <div class="tier-content">
                <div class="tier-top">
                  <div class="tier-info">
                    <h4 class="tier-name">{{ tier.name }}</h4>
                    <p class="tier-price" v-if="tier.feeType === 'fixed'">
                      ${{ tier.price }} per ticket
                    </p>
                    <p class="tier-price" v-if="tier.feeType === 'sliding'">
                      ${{ getSlidingPrice(tier) }} per ticket
                    </p>
                    <p class="tier-price" v-if="tier.feeType === 'free'">
                      Free!
                    </p>
                  </div>

                  <div class="quantity-picker">
                    <span class="quantity-label">Quantity</span>
                    <div class="quantity-controls">
                      <button
                        class="qty-btn minus"
                        @click="decrementQuantity(tier)"
                        :disabled="!tier.quantity || tier.quantity <= 0"
                      >
                        <span>&minus;</span>
                      </button>
                      <input
                        type="number"
                        class="qty-input"
                        v-model.number="tier.quantity"
                        min="0"
                        :max="maxSale"
                        readonly
                      />
                      <button
                        class="qty-btn plus"
                        @click="incrementQuantity(tier)"
                        :disabled="totalQuantity >= maxSale"
                      >
                        <span>+</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div v-if="tier.feeType === 'sliding'" class="slider-section">
                  <div class="slider-container">
                    <div
                      class="floating-price"
                      :style="getFloatingPriceStyle(tier)"
                    >
                      ${{ getSlidingPrice(tier) }}
                    </div>
                    <input
                      type="range"
                      class="price-slider"
                      :min="tier.minPrice"
                      :max="tier.maxPrice"
                      :value="getSlidingPrice(tier)"
                      @input="updateSlidingPrice(tier, $event.target.value)"
                    />
                    <div class="slider-labels">
                      <span>${{ tier.minPrice }}</span>
                      <span>${{ tier.maxPrice }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="summary-column">
          <h4 class="order-summary-title">ORDER SUMMARY</h4>
          <div class="order-summary-box">
            <div class="tier-subtotals">
              <div
                v-for="tier in priceTiers"
                :key="'subtotal-' + tier.id"
                class="tier-subtotal-row"
                :class="{
                  'tier-subtotal-row-sliding': tier.feeType === 'sliding'
                }"
              >
                <span class="tier-subtotal">${{ getTierSubtotal(tier) }}</span>
              </div>
            </div>

            <div class="order-details">
              <div class="order-row">
                <span>Subtotal</span>
                <span>${{ subtotal }}</span>
              </div>
              <div class="order-row">
                <span>Sales Tax</span>
                <span>${{ salesTax }}</span>
              </div>
            </div>

            <div class="order-total-row">
              <span>Total</span>
              <span>${{ orderTotal }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="walkup-footer">
        <button
          class="button is-primary is-fullwidth checkout-btn"
          @click="goToStep2"
          :disabled="totalQuantity === 0"
        >
          Checkout
        </button>
      </div>
    </div>

    <!-- Step 2: Checkout / Patron Info -->
    <div v-if="step === 2" class="walkup-step-2">
      <div class="checkout-content">
        <h1>Checkout</h1>

        <div class="field">
          <label class="label">First Name (optional)</label>
          <div class="control">
            <input
              class="input"
              type="text"
              placeholder="First Name"
              v-model="firstName"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">Last Name (optional)</label>
          <div class="control">
            <input
              class="input"
              type="text"
              placeholder="Last Name"
              v-model="lastName"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">Email (optional)</label>
          <div class="control">
            <input
              class="input"
              type="email"
              placeholder="Email"
              v-model="email"
            />
          </div>
        </div>

        <div class="field">
          <label class="label">Note (optional)</label>
          <div class="control">
            <input
              class="input"
              type="text"
              placeholder="Add artist's name and/or the guest's relationship to the artists"
              v-model="note"
            />
          </div>
        </div>

        <div class="field checkin-field">
          <label class="checkbox">
            <input type="checkbox" v-model="shouldCheckIn" />
            <span class="checkin-label">Check In</span>
          </label>
        </div>

        <div class="checkout-buttons">
          <button class="button is-primary back-btn" @click="goToStep1">
            Back
          </button>
          <template v-if="parseFloat(orderTotal) > 0">
            <button class="button is-primary cash-btn" @click="add('Cash')">
              Cash
            </button>
            <button class="button is-primary credit-btn" @click="add('Credit')">
              Credit
            </button>
          </template>
          <button
            v-else
            class="button is-primary add-walkup-btn"
            @click="add('Free')"
          >
            Add Walkup
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import store from "@/store";
import firebase from "firebase/app";
import moment from "moment";

export default {
  name: "Walkup",
  data() {
    return {
      step: 1,
      firstName: "",
      lastName: "",
      email: "",
      note: "",
      shouldCheckIn: false
    };
  },
  computed: {
    ...mapGetters({
      priceTiers: "priceTiers",
      dateSummary: "dateSummary",
      event: "event"
    }),
    maxSale() {
      return this.dateSummary.inventory || 100;
    },
    totalQuantity() {
      return this.priceTiers.reduce(
        (sum, tier) => sum + (tier.quantity || 0),
        0
      );
    },
    subtotal() {
      let total = 0;
      this.priceTiers.forEach(tier => {
        if (tier.quantity && tier.feeType === "fixed") {
          total += tier.price * tier.quantity;
        } else if (tier.quantity && tier.feeType === "sliding") {
          const price =
            tier.slidingPrice !== undefined ? tier.slidingPrice : tier.minPrice;
          total += price * tier.quantity;
        }
      });
      return total.toFixed(2);
    },
    salesTax() {
      if (this.event && this.event.chargeSalesTax && this.event.salesTaxRate) {
        return (
          parseFloat(this.subtotal) *
          (this.event.salesTaxRate / 100)
        ).toFixed(2);
      }
      return "0";
    },
    orderTotal() {
      return (parseFloat(this.subtotal) + parseFloat(this.salesTax)).toFixed(2);
    },
    isCurrentDay() {
      if (!this.dateSummary || !this.dateSummary.startDate) return false;
      const today = moment().format("YYYY-MM-DD");
      return this.dateSummary.startDate === today;
    }
  },
  async created() {
    console.log("Get price tiers.");
    await store.dispatch("fetchPriceTiers");
    // Initialize quantities to 0 and sliding prices
    this.priceTiers.forEach(tier => {
      if (tier.quantity === undefined) {
        this.$set(tier, "quantity", 0);
      }
      // Initialize sliding price to minPrice for sliding scale tiers
      if (tier.feeType === "sliding" && tier.slidingPrice === undefined) {
        this.$set(tier, "slidingPrice", tier.minPrice);
        this.$set(tier, "price", tier.minPrice);
      }
    });
    // Default check-in to true if performance is on current day
    this.shouldCheckIn = this.isCurrentDay;
  },
  methods: {
    close() {
      this.$emit("canceled");
    },
    incrementQuantity(tier) {
      if (this.totalQuantity < this.maxSale) {
        if (!tier.quantity) {
          this.$set(tier, "quantity", 1);
        } else {
          tier.quantity++;
        }
      }
    },
    decrementQuantity(tier) {
      if (tier.quantity && tier.quantity > 0) {
        tier.quantity--;
      }
    },
    getTierSubtotal(tier) {
      if (!tier.quantity || tier.feeType === "free") {
        return "0";
      }
      const price =
        tier.feeType === "sliding" ? this.getSlidingPrice(tier) : tier.price;
      return (price * tier.quantity).toFixed(0);
    },
    getSlidingPrice(tier) {
      return tier.slidingPrice !== undefined
        ? tier.slidingPrice
        : tier.minPrice;
    },
    updateSlidingPrice(tier, value) {
      const price = parseInt(value, 10);
      this.$set(tier, "slidingPrice", price);
      this.$set(tier, "price", price);
    },
    getFloatingPriceStyle(tier) {
      const min = tier.minPrice;
      const max = tier.maxPrice;
      const current = this.getSlidingPrice(tier);
      const percentage = ((current - min) / (max - min)) * 100;
      // Offset to center the label over the thumb
      return {
        left: `calc(${percentage}% - 20px + ${(50 - percentage) * 0.3}px)`
      };
    },
    goToStep2() {
      if (this.totalQuantity > 0) {
        this.step = 2;
      }
    },
    goToStep1() {
      this.step = 1;
    },
    async add(method) {
      var db = firebase.firestore();

      this.priceTiers.forEach(tier => {
        if (tier.quantity) {
          let price = 0;
          if (tier.feeType == "fixed" || tier.feeType == "sliding")
            price = tier.price;

          const checkInQty = this.shouldCheckIn ? tier.quantity : 0;
          const status = this.shouldCheckIn ? "checkedin" : "paid";

          const lineItem = {
            checkedin: checkInQty,
            firstName: this.firstName || method,
            lastName: this.lastName || "Walkup",
            email: this.email || "",
            discountCode: this.note || "",
            tierId: tier.id,
            tierName: tier.name,
            quantity: tier.quantity,
            price: price,
            total: price * tier.quantity,
            status: status
          };

          db.collection("events")
            .doc(this.event.id)
            .collection("userData")
            .doc("audienceReport")
            .collection("dates")
            .doc(this.dateSummary.dateId)
            .collection("audience")
            .add(lineItem)
            .catch(function(error) {
              console.error("Error adding walkup: ", error);
            });
          tier.quantity = 0;
        }
      });

      // Emit walkupAdded event and reset form
      this.$emit("walkupAdded");
      this.resetForm();
    },
    resetForm() {
      this.step = 1;
      this.firstName = "";
      this.lastName = "";
      this.email = "";
      this.note = "";
      this.shouldCheckIn = this.isCurrentDay;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.walkup-modal {
  background: white;
  border-radius: $radius;
  position: relative;
}

// Step 1 Styles
.walkup-step-1 {
  display: flex;
  flex-direction: column;
  min-height: 500px;
}

.walkup-header {
  padding: 30px 40px 20px 40px;

  h1 {
    font-family: $family-primary;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 36px;
    margin-bottom: 0;
    line-height: 1;
  }
}

.order-summary-title {
  font-family: $family-primary;
  font-weight: 500;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: $gray3;
  margin-bottom: 10px;
}

// Body section with tiers and summary side by side
.walkup-body {
  display: flex;
  padding: 0 40px 20px 40px;
}

.tiers-column {
  flex: 2;
  padding-right: 40px;
  padding-top: 30px; // Push down to align with gray box
}

.summary-column {
  flex: 1;
  min-width: 200px;
}

.order-summary-box {
  background: $gray6;
  border-radius: $radius;
  padding: 0;
}

.tier-subtotals {
  border-bottom: 1px solid white;
}

.tier-subtotal-row {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  min-height: 110px; // Match tier-row height
  box-sizing: border-box;
  border-bottom: 1px solid white;

  &:last-child {
    border-bottom: none;
  }
}

.tier-subtotal-row-sliding {
  min-height: 205px; // Match sliding tier-row height
}

.tier-subtotal {
  font-family: $family-primary;
  font-size: 24px;
  font-weight: 500;
}

.order-details {
  border-bottom: 1px solid white;
  padding: 15px 20px;
}

.order-row {
  display: flex;
  justify-content: space-between;
  font-family: $family-secondary;
  font-size: 14px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.order-total-row {
  display: flex;
  justify-content: space-between;
  font-family: $family-primary;
  font-weight: 500;
  font-size: 16px;
  padding: 15px 20px;
}

// Tier List Styles
.tier-list {
  border-top: 1px solid $gray5;
}

.tier-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
  min-height: 110px;
  box-sizing: border-box;
  border-bottom: 1px solid $gray5;
}

.tier-row-sliding {
  min-height: 205px;
  align-items: flex-start;
}

.tier-content {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.tier-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.tier-info {
  flex: 1;
}

.tier-name {
  font-family: $family-primary;
  font-weight: 700;
  font-size: 18px;
  margin-bottom: 4px;
}

.tier-price {
  font-family: $family-secondary;
  font-size: 14px;
  color: $gray2;
  margin: 0;
}

// Sliding Scale Slider
.slider-section {
  margin-top: 15px;
  padding-right: 20px;
}

.slider-container {
  position: relative;
  padding-top: 30px;
}

.floating-price {
  position: absolute;
  top: 0;
  font-family: $family-primary;
  font-weight: 700;
  font-size: 16px;
  white-space: nowrap;
}

.price-slider {
  width: 100%;
  height: 4px;
  -webkit-appearance: none;
  appearance: none;
  background: $gray5;
  border-radius: 2px;
  outline: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $active-green;
    cursor: pointer;
    border: none;
  }

  &::-moz-range-thumb {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: $active-green;
    cursor: pointer;
    border: none;
  }

  &::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 2px;
  }

  &::-moz-range-track {
    height: 4px;
    border-radius: 2px;
    background: $gray5;
  }
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
  font-family: $family-secondary;
  font-size: 14px;
  color: $gray2;
}

// Quantity Picker
.quantity-picker {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.quantity-label {
  font-family: $family-secondary;
  font-size: 12px;
  color: $gray3;
  margin-bottom: 8px;
}

.quantity-controls {
  display: flex;
  align-items: center;
}

.qty-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: none;
  font-size: 24px;
  cursor: pointer;
  color: $gray1;
  display: flex;
  align-items: center;
  justify-content: center;

  &:disabled {
    color: $gray4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    color: $active-green;
  }

  span {
    line-height: 1;
  }
}

.qty-input {
  width: 50px;
  height: 40px;
  border: 1px solid $gray5;
  border-radius: $radius;
  text-align: center;
  font-family: $family-secondary;
  font-size: 16px;
  font-weight: bold;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

// Footer
.walkup-footer {
  padding: 20px 40px 30px;
}

.checkout-btn {
  background-color: $active-green !important;
  border-color: $active-green !important;
  font-weight: 500;
  text-transform: uppercase;
  height: 50px;
  font-size: 14px;

  &:disabled {
    background-color: $gray4 !important;
    border-color: $gray4 !important;
  }
}

// Step 2 Styles
.walkup-step-2 {
  min-height: 500px;
}

.checkout-content {
  padding: 0 40px 30px 40px;

  h1 {
    font-family: $family-primary;
    font-weight: 800;
    text-transform: uppercase;
    font-size: 36px;
    margin-bottom: 30px;
    line-height: 1;
  }
}

.field {
  margin-bottom: 20px;
}

.label {
  font-family: $family-secondary;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.input {
  background-color: $gray6;
  border: none;
  height: 50px;
  font-family: $family-secondary;

  &::placeholder {
    color: $gray4;
  }

  &:focus {
    border-color: $active-green;
    box-shadow: none;
  }
}

.checkin-field {
  margin-top: 25px;
  margin-bottom: 30px;

  .checkbox {
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  input[type="checkbox"] {
    margin-right: 10px;
  }

  .checkin-label {
    font-family: $family-secondary;
    font-size: 16px;
  }
}

.checkout-buttons {
  display: flex;
  gap: 15px;

  .button {
    flex: 1;
    height: 50px;
    font-weight: 500;
    text-transform: uppercase;
    font-size: 14px;
  }

  .back-btn {
    background-color: $active-green !important;
    border-color: $active-green !important;
  }

  .cash-btn {
    background-color: $active-green !important;
    border-color: $active-green !important;
  }

  .credit-btn {
    background-color: $active-green !important;
    border-color: $active-green !important;
  }

  .add-walkup-btn {
    flex: 2;
    background-color: $active-green !important;
    border-color: $active-green !important;
  }
}

// Responsive
@media screen and (max-width: 768px) {
  .walkup-header {
    padding: 20px;

    h1 {
      font-size: 28px;
    }
  }

  .walkup-body {
    flex-direction: column;
    padding: 0 20px 20px 20px;
  }

  .tiers-column {
    padding-right: 0;
    padding-top: 0;
    margin-bottom: 20px;
  }

  .summary-column {
    min-width: 100%;
  }

  .walkup-footer {
    padding: 20px;
  }

  .checkout-content {
    padding: 0 20px 20px 20px;

    h1 {
      font-size: 28px;
    }
  }

  .checkout-buttons {
    flex-direction: column;
  }
}
</style>
